import { ChangeEvent, useMemo, useState } from "react";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/free-solid-svg-icons/faEyeSlash";
import {
    SelectOption
} from "@iddataweb/idw-common-ui/types";
import {
    LocaleTypes
} from "@iddataweb/idw-common-ui/enums";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { WorkflowGeneralBasicInfoValidationErrors } from "@idw-types/common/validation/workflow-details-validation-errors";
import { FormattedIdwProfile } from "@idw-types/data/idw-profile";
import { FormattedWorkflowDetailsConfiguration } from "@idw-types/data/workflow-details-configuration";
import { sortStrings } from "@utils/string-util";
import { mapEntityWithNameAndIdToSelectOption } from "@utils/select-option-mapping-util";
import {
    handleAddWorkflowRedirect,
    handleRemoveWorkflowRedirect,
    handleWorkflowRedirectChangeAtIndex
} from "@utils/redirect-urls/redirect-urls-util";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { OrganizationSelectFormGroup, RelyingPartySelectFormGroup } from "@containers/common/select-form-groups";
import { WorkflowRedirectUrlContainer } from "@containers/workflow-details/workflow-details-component/workflow-details-general-tab/basic-info-tab-container/workflow-redirect-url-container/workflow-redirect-url-container";
import { PageLevelTypes } from "@enums/page-level-types";
import { RedirectUrlInformation } from "@idw-types/redirect-url-information";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { RedirectUrlOriginTypes } from "@enums/redirect-url-origin-types";

type BasicInfoTabContainerProps = {
    isAdminUser: boolean;
    isDisabled: boolean;
    validationErrors: WorkflowGeneralBasicInfoValidationErrors;
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
    primaryRpService: FormattedRpServiceConfiguration;
    rpServiceConfigurations: Array<FormattedRpServiceConfiguration>;
    workflowProfile: FormattedIdwProfile;
    workflowDetailsConfigurationState: FormattedWorkflowDetailsConfiguration;
    workflowProfileKey: string;
    isMultiLanguageEnabled: boolean;
    availableLanguages: Array<LocaleTypes>;
    redirectUrlsInformation: RedirectUrlInformation;
    setRpServiceConfigurationsState: (state: Array<FormattedRpServiceConfiguration>) => void;
    setWorkflowDetailsConfigurationState: (state: FormattedWorkflowDetailsConfiguration) => void;
    setWorkflowProfile: (profile: FormattedIdwProfile) => void;
};

type BasicInfoTabContainerState = {
    showApiKey: boolean;
    showSharedSecret: boolean;
};

export const BasicInfoTabContainer = (props: BasicInfoTabContainerProps) => {
    const {
        isAdminUser,
        isDisabled,
        validationErrors,
        axnCompanies,
        axnRelyingParties,
        primaryRpService,
        rpServiceConfigurations,
        workflowProfile,
        workflowDetailsConfigurationState,
        workflowProfileKey,
        redirectUrlsInformation,
        isMultiLanguageEnabled,
        availableLanguages,
        setRpServiceConfigurationsState,
        setWorkflowDetailsConfigurationState,
        setWorkflowProfile
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [state, setState] = useState<BasicInfoTabContainerState>({
        showApiKey: false,
        showSharedSecret: false
    });

    const allowedRelyingParties = useMemo(() => {
        return axnRelyingParties.filter(relyingParty =>
        (relyingParty.companyID === workflowDetailsConfigurationState.company.id
            && relyingParty.status === EntityStatusTypes.ACTIVE)
        );
    }, [axnRelyingParties, workflowDetailsConfigurationState.company]);

    const selectedRelyingParty = useMemo(() => {
        return mapEntityWithNameAndIdToSelectOption(workflowDetailsConfigurationState.relyingParty);
    }, [workflowDetailsConfigurationState.relyingParty]);

    const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setWorkflowDetailsConfigurationState({
            ...workflowDetailsConfigurationState,
            name: event.target.value
        });
    };

    const toggleShowApiKey = () => {
        setState({ ...state, showApiKey: !state.showApiKey });
    };

    const toggleShowSharedSecret = () => {
        setState({ ...state, showSharedSecret: !state.showSharedSecret });
    };

    const handleApiKeyChange = async (newOption: SelectOption) => {
        if (newOption) {
            const newRpServiceWorkflows = workflowDetailsConfigurationState.rpServiceWorkflows.map(rpsw => {
                if (rpsw.rpAXNServiceID === newOption.value) {
                    return {
                        ...rpsw,
                        primaryServiceFlag: "true"
                    }
                } else {
                    return {
                        ...rpsw,
                        primaryServiceFlag: "false"
                    }
                }
            });

            setWorkflowDetailsConfigurationState({
                ...workflowDetailsConfigurationState,
                rpServiceWorkflows: newRpServiceWorkflows
            });
        }
    };

    const handleRelyingPartyChange = (option: SelectOption) => {
        const relyingParty = allowedRelyingParties.find(rp => rp.id === option.value);
        const updatedRpServiceConfigurations = rpServiceConfigurations.map(rpService => ({ ...rpService, relyingParty }));

        setWorkflowDetailsConfigurationState({
            ...workflowDetailsConfigurationState,
            relyingParty
        });

        setRpServiceConfigurationsState(updatedRpServiceConfigurations);
    }

    const apiKeyOptions: Array<SelectOption> = useMemo(() => {
        const options: Array<SelectOption> = [];
        workflowDetailsConfigurationState.rpServiceWorkflows.forEach(rpsw => {
            const rpService = rpServiceConfigurations.find(rps => rps.id === rpsw.rpAXNServiceID);

            if (rpService) {
                options.push({
                    label: state.showApiKey ? `${rpService.serviceName} (${rpService.apiKey})` : "XXXXXXXXXXXXXX",
                    value: rpsw.rpAXNServiceID
                });
            }
        });
        return options.sort((a, b) => sortStrings(a.label, b.label));
    }, [workflowDetailsConfigurationState.rpServiceWorkflows, state.showApiKey, rpServiceConfigurations]);

    const selectedPrimaryApiKeyOption: SelectOption = useMemo(() => {
        return {
            label: state.showApiKey ? `${primaryRpService.serviceName} (${primaryRpService.apiKey})` : "XXXXXXXXXXXXXX",
            value: primaryRpService.id
        };
    }, [primaryRpService, state.showApiKey]);

    const resource = resources.pages.workflowDetails.tabs.general.subTabs.basicInfo;

    const handleRedirectChangeAtIndex = (newRedirectUrl: string, index: number, redirectUrl: string, redirectUrlOrigin: RedirectUrlOriginTypes) => {
        handleWorkflowRedirectChangeAtIndex(
            workflowProfile,
            workflowProfileKey,
            index,
            redirectUrlOrigin,
            newRedirectUrl,
            redirectUrl,
            primaryRpService,
            rpServiceConfigurations,
            isMultiLanguageEnabled,
            availableLanguages,
            setWorkflowProfile,
            setRpServiceConfigurationsState
        );
    };

    const handleRemoveRedirect = (index: number, redirectUrlOrigin: RedirectUrlOriginTypes) => {
        handleRemoveWorkflowRedirect(
            workflowProfile,
            workflowProfileKey,
            index,
            redirectUrlOrigin,
            redirectUrlsInformation,
            primaryRpService,
            rpServiceConfigurations,
            setWorkflowProfile,
            setRpServiceConfigurationsState
        );
    };

    const handleAddRedirect = (newRedirectUrl: string) => {
        handleAddWorkflowRedirect(
            workflowProfile,
            workflowProfileKey,
            newRedirectUrl,
            primaryRpService,
            rpServiceConfigurations,
            setWorkflowProfile,
            setRpServiceConfigurationsState
        );
    };

    const selectedOrganization = useMemo(() => {
        if (workflowDetailsConfigurationState.relyingParty) {
            const organization = axnCompanies.find(axnCompany => axnCompany.id === workflowDetailsConfigurationState.relyingParty.companyID);
            if (organization) {
                return mapEntityWithNameAndIdToSelectOption(organization);
            }
        }
        
        return null;
    }, [workflowDetailsConfigurationState.relyingParty, axnCompanies]);

    return (
        <div>
            <ContentPageHeader title={resource.title} />
            <ContentPageBody isMaxWidth={true}>
                <InputFormGroup
                    controlId={"workflow-details-service-name"}
                    automationId={"workflow-details-service-name"}
                    label={resource.inputs.workflowName.label}
                    ariaLabel={resource.inputs.workflowName.label}
                    value={workflowDetailsConfigurationState.name}
                    disabled={isDisabled}
                    isRequired={true}
                    labelWidth={labelWidth}
                    placeholder={resource.inputs.workflowName.placeholder}
                    onChange={handleNameChange}
                />
                {validationErrors.workflowNameError}

                <OrganizationSelectFormGroup
                    idPrefix="workflow-details"
                    onChange={() => { }}
                    axnCompanies={axnCompanies}
                    selectedOption={selectedOrganization}
                    disabled={true}
                    isRequired={false}
                    isSearchable={true}
                    isClearable={true}
                    labelWidth={labelWidth}
                />

                <RelyingPartySelectFormGroup
                    idPrefix={"workflow-details"}
                    axnRelyingParties={allowedRelyingParties}
                    disabled={!isAdminUser || isDisabled}
                    isClearable={false}
                    isRequired={true}
                    isSearchable={true}
                    labelWidth={labelWidth}
                    onChange={handleRelyingPartyChange}
                    selectedOption={selectedRelyingParty}
                />

                <SelectFormGroup
                    controlId={"workflow-details-api-key"}
                    automationId={"workflow-details-api-key"}
                    label={resource.inputs.apiKey.label}
                    ariaLabel={resource.inputs.apiKey.label}
                    selectedOption={selectedPrimaryApiKeyOption}
                    options={apiKeyOptions}
                    disabled={
                        isDisabled ||
                        !state.showApiKey
                    }
                    isRequired={true}
                    labelWidth={labelWidth}
                    placeholder={resource.inputs.apiKey.placeholder}
                    onChange={handleApiKeyChange}
                    isSearchable={true}
                    isClearable={false}
                    afterInputIconDisabled={false}
                    afterInputIcon={state.showApiKey ? faEyeSlash : faEye}
                    afterInputIconOnClick={toggleShowApiKey}
                />
                <InputFormGroup
                    controlId={"workflow-details-shared-secret"}
                    automationId={"workflow-details-shared-secret"}
                    label={resource.inputs.sharedSecret.label}
                    ariaLabel={resource.inputs.sharedSecret.label}
                    value={state.showSharedSecret ? primaryRpService.sharedSecret : "XXXXXXXXXXXXXX"}
                    disabled={true} //{isDisabled || !state.showSharedSecret}
                    isRequired={true}
                    labelWidth={labelWidth}
                    placeholder={resource.inputs.sharedSecret.placeholder}
                    onChange={() => { }} //{handleSharedSecretChange}
                    afterInputIconDisabled={false}
                    afterInputIcon={state.showSharedSecret ? faEyeSlash : faEye}
                    afterInputIconOnClick={toggleShowSharedSecret}
                />
            </ContentPageBody>
            <WorkflowRedirectUrlContainer
                isDisabled={isDisabled}
                pageLevel={PageLevelTypes.WORKFLOW}
                validationErrors={validationErrors}
                redirectUrlsInformation={redirectUrlsInformation}
                handleRemoveRedirect={handleRemoveRedirect}
                handleAddRedirect={handleAddRedirect}
                handleRedirectChangeAtIndex={handleRedirectChangeAtIndex}
            />
        </div>
    );
};